import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useSwiperSlide } from "swiper/react";
import { Rule } from "ui/rule";
import { ChakraBox } from "ui/chakra-framer";
import { type SliderCardProps } from "./slider-data";

const scaleCard = {
  initial: {
    scale: 0.85,
  },
  animate: {
    scale: 1,
    transition: { duration: 0.15 },
  },
};

export function SliderCard({ icon, title, description, integrations, integrationsDescription }: SliderCardProps) {
  const swiperSlide = useSwiperSlide();
  const isExpanded = swiperSlide?.isActive;

  return (
    <ChakraBox
      layout
      bg="white"
      borderRadius={{ base: "small", md: "bigger" }}
      boxShadow="card"
      flexDir="column"
      height="auto"
      justifyContent="center"
      mb="biggest"
      p={{ base: "small", sm: "bigger" }}
      w={{ base: "330px", sm: "460px" }}
      variants={scaleCard}
      initial="initial"
      animate={isExpanded ? "animate" : "initial"}
    >
      {/* Using layout animation here somehow makes the card text transition smoother, even though it's the parent that is animating */}
      <ChakraBox
        layout
        display="flex"
        alignItems="center"
        gap="smaller"
        // @ts-expect-error no problem in framer transition, although type error appears: https://chakra-ui.com/getting-started/with-framer
        transition={{ duration: 0.1 }}
      >
        <Flex
          align="center"
          bg={icon.bgColor}
          color={icon.color}
          borderRadius="10px"
          boxSize={{ base: "40px", sm: "48px" }}
          flexShrink={0}
          justify="center"
        >
          <Box as={icon.icon} boxSize={{ base: "icon.md", sm: "icon.lg" }} />
        </Flex>

        <Box>
          <Text
            color={isExpanded ? "text.primary" : "brand.textSecondary.light"}
            textStyle={{ base: "caption1", sm: "body3" }}
            fontWeight="semibold"
            transition="all .1s linear"
          >
            {title}
          </Text>

          <Text color="brand.textSecondary.light" textStyle={{ base: "caption2", sm: "caption1" }} mt="tinier">
            {description}
          </Text>
        </Box>
      </ChakraBox>

      <Box display={{ base: "block", sm: isExpanded ? "block" : "none" }}>
        <Rule />
        <Flex align="center" gap="small">
          {/* Only display the first 3 icons on mobile */}
          <Flex display={{ base: "flex", sm: "none" }} align="center" gap="smallest">
            {integrations.slice(0, 3).map((integration) => (
              <Box key={integration.name} as={integration.icon} boxSize="icon.lg" />
            ))}
          </Flex>

          <Flex display={{ base: "none", sm: "flex" }} align="center" gap="smallest">
            {integrations.map((integration) => (
              <Box key={integration.name} as={integration.icon} boxSize="icon.xl" />
            ))}
          </Flex>

          <Text color="brand.textSecondary.light" textStyle="caption2">
            {integrationsDescription}
          </Text>
        </Flex>
      </Box>
    </ChakraBox>
  );
}
